import type { CSSProperties, MouseEvent, ReactNode } from 'react'
import { cn } from '@/lib/utils'

export type HomeWidgetShellProps = {
  title?: ReactNode
  icon?: ReactNode
  /** Trailing header control (e.g. View All) */
  action?: ReactNode
  children: ReactNode
  footer?: ReactNode
  className?: string
  contentClassName?: string
  'data-testid'?: string
}

/**
 * Shared card chrome for Global Home widgets - one surface language for all sections.
 */
export function HomeWidgetShell({
  title,
  icon,
  action,
  children,
  footer,
  className,
  contentClassName,
  'data-testid': dataTestId,
}: HomeWidgetShellProps) {
  const showHeader = title != null || icon != null || action != null

  return (
    <section
      className={cn(
        'flex flex-col min-h-0 rounded-lg border border-border/40 bg-card p-4',
        className,
      )}
      data-testid={dataTestId}
    >
      {showHeader ? (
        <div className="mb-3 flex items-center justify-between gap-2 min-w-0">
          <h2 className="text-sm font-semibold text-foreground flex items-center gap-2 min-w-0">
            {icon ? (
              <span className="shrink-0 text-muted-foreground [&_svg]:h-4 [&_svg]:w-4">
                {icon}
              </span>
            ) : null}
            <span className="truncate">{title}</span>
          </h2>
          {action ? (
            <div className="shrink-0 text-xs text-muted-foreground [&_button]:text-xs [&_button]:text-muted-foreground [&_button]:hover:text-foreground">
              {action}
            </div>
          ) : null}
        </div>
      ) : null}
      <div className={cn('flex flex-1 flex-col min-h-0', contentClassName)}>{children}</div>
      {footer ? <div className="mt-3 shrink-0">{footer}</div> : null}
    </section>
  )
}

export type HomeFeedListProps = {
  children: ReactNode
  className?: string
}

export function HomeFeedList({ children, className }: HomeFeedListProps) {
  return (
    <ul className={cn('divide-y divide-border/40 flex-1 min-h-0', className)}>{children}</ul>
  )
}

export type HomeFeedRowProps = {
  title: ReactNode
  meta?: ReactNode
  trailing?: ReactNode
  /** Optional leading column (e.g. document icon). */
  leading?: ReactNode
  selected?: boolean
  disabled?: boolean
  /** Native tooltip - must be user-facing (never opaque ws/hex paths). */
  titleAttr?: string
  onClick?: (event: MouseEvent<HTMLButtonElement>) => void
  onDoubleClick?: (event: MouseEvent<HTMLButtonElement>) => void
  onContextMenu?: (event: MouseEvent<HTMLButtonElement>) => void
  className?: string
  style?: CSSProperties
  'data-testid'?: string
}

/**
 * Shared list row for Home / workspace-dashboard feeds.
 * Selected chrome matches ExtensionListRow: accent wash + 2px primary rail.
 */
export function HomeFeedRow({
  title,
  meta,
  trailing,
  leading,
  selected = false,
  disabled = false,
  titleAttr,
  onClick,
  onDoubleClick,
  onContextMenu,
  className,
  style,
  'data-testid': dataTestId,
}: HomeFeedRowProps) {
  return (
    <li>
      <button
        type="button"
        disabled={disabled}
        title={titleAttr}
        onClick={onClick}
        onDoubleClick={onDoubleClick}
        onContextMenu={onContextMenu}
        data-testid={dataTestId}
        data-selected={selected ? 'true' : undefined}
        aria-selected={selected || undefined}
        style={style}
        className={cn(
          // pl-3: breathing room from workspace origin rail (border-l-2) to icon/text
          'group relative w-full text-left pl-3 pr-2.5 py-2.5 transition-colors',
          'hover:bg-accent/40 focus-visible:outline-none focus-visible:bg-accent/40',
          selected &&
            'bg-accent hover:bg-accent before:pointer-events-none before:absolute before:inset-y-0 before:left-0 before:w-0.5 before:bg-primary',
          disabled && 'opacity-60',
          className,
        )}
      >
        <div className="flex items-start gap-2.5 min-w-0">
          {leading != null ? (
            <div className="shrink-0 mt-0.5 text-muted-foreground [&_svg]:h-4 [&_svg]:w-4">
              {leading}
            </div>
          ) : null}
          <div className="flex items-start justify-between gap-2 min-w-0 flex-1 overflow-hidden">
            <div className="min-w-0 flex-1 overflow-hidden">
              <div className="text-sm text-foreground truncate">{title}</div>
            {meta != null ? (
              <div className="mt-0.5 text-xs text-muted-foreground min-w-0 truncate overflow-hidden">
                {meta}
              </div>
            ) : null}
            </div>
            {trailing != null ? (
              <div className="shrink-0 pt-0.5 text-xs text-muted-foreground">{trailing}</div>
            ) : null}
          </div>
        </div>
      </button>
    </li>
  )
}
